/**
 * Place Order API
 * APIs for placing restaurant orders and fetching menu/site data
 * 
 * Base URL: https://api.innoworx.ai/Stage
 */

import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || 'https://api.innoworx.ai/Stage';

/**
 * Function to get the authentication token.
 */
const getAuthToken = async () => {
  try {
    const { tokens } = await fetchAuthSession();
    if (tokens?.idToken) {
      const idToken = tokens.idToken;
      const currentTime = Math.floor(Date.now() / 1000);
      if (idToken.payload.exp > currentTime) return tokens.idToken.toString();
    }
  } catch (e) {
    console.error('PlaceOrder API: auth error', e);
  }
  return null;
};

/**
 * Generic function to make authenticated API requests.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing. Please ensure a user is logged in.');
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || errorData.message || `API Error: ${response.status}`;
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_place_order',
        `Place Order API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[placeOrderApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      const corsError = new Error(
        `CORS Error: Unable to connect to the API. ` +
        'The API Gateway needs to be configured to allow CORS from your origin. ' +
        'Please contact the backend team to ensure CORS headers are properly configured.'
      );
      corsError.isCorsError = true;
      corsError.endpoint = endpoint;
      
      // Log CORS errors to logging service
      loggingService.logError(
        'api_error_place_order',
        `Place Order API CORS/network error for ${endpoint}`,
        corsError,
        {
          endpoint,
          method,
          errorType: 'CORS_ERROR',
        }
      ).catch(logError => {
        console.warn('[placeOrderApi] Failed to log error:', logError);
      });
      
      throw corsError;
    }
    
    // Log network/fetch errors to logging service
    if (error.status === undefined) {
      loggingService.logError(
        'api_error_place_order',
        `Place Order API network/fetch error for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          errorType: error.name || 'NetworkError',
        }
      ).catch(logError => {
        console.warn('[placeOrderApi] Failed to log error:', logError);
      });
    }
    
    throw error;
  }
};

/**
 * Get site dataset for restaurant analytics (NOT for getting site list).
 * GET /restaurant/site-dataset?start_date={start_date}&end_date={end_date}
 * Lambda: innoworx-restaurant-get-sites-dataset
 * 
 * NOTE: This endpoint returns analytics data (order counts, values) for a date range,
 * NOT a list of sites for selection. Use getAllSites from siteApi.js for site selection.
 * 
 * @param {string} [start_date] - Start date in ISO datetime format (YYYY-MM-DDTHH:mm:ss). Defaults to today 00:00:00 if not provided.
 * @param {string} [end_date] - End date in ISO datetime format (YYYY-MM-DDTHH:mm:ss). Defaults to today 23:59:59 if not provided.
 * @returns {Promise<Array>} Array of site analytics objects: { site_id, site_name, address, order_count, total_order_value, hourly_throughput }
 */
export const getSiteDataset = async (start_date = null, end_date = null) => {
  // Default to today's date range if not provided (ISO datetime format)
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const startDate = start_date || `${today}T00:00:00`;
  const endDate = end_date || `${today}T23:59:59`;

  const params = new URLSearchParams();
  params.set('start_date', startDate);
  params.set('end_date', endDate);
  
  const endpoint = `/restaurant/site-dataset?${params.toString()}`;
  const response = await makeApiRequest(endpoint, 'GET');
  const data = await response.json();
  
  // Log API event for successful fetch
  loggingService.logInfo(
    'api_success_place_order',
    `Successfully fetched site dataset`,
    {
      endpoint: '/restaurant/site-dataset',
      method: 'GET',
      startDate,
      endDate,
      count: Array.isArray(data) ? data.length : 0,
    }
  ).catch(logError => {
    console.warn('[placeOrderApi] Failed to log success event:', logError);
  });
  
  return Array.isArray(data) ? data : [];
};

/**
 * Get today's date in YYYY-MM-DD.
 */
const getTodayDate = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Get current shift for a site.
 * Tries dashboard first (GET /dashboard/site/shifts) to avoid 404 from the non-existent
 * /shift/current-for-site endpoint; if dashboard returns no shift, tries that endpoint.
 *
 * @param {string} site_id - The site ID
 * @returns {Promise<Object|null>} Shift object with ss_id, or null if no shift
 * @throws {Error} Only throws for auth/network errors
 */
export const getCurrentShiftForSite = async (site_id) => {
  if (!site_id) {
    throw new Error('site_id is required');
  }

  const token = await getAuthToken();
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  // Try dashboard first so we don't hit 404 in console when /shift/current-for-site is not implemented
  const fromDashboard = await getCurrentShiftForSiteFallback(site_id);
  if (fromDashboard) {
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_place_order',
      `Successfully fetched current shift for site`,
      {
        endpoint: '/dashboard/site/shifts',
        method: 'GET',
        siteId: site_id,
        shiftId: fromDashboard.ss_id,
      }
    ).catch(logError => {
      console.warn('[placeOrderApi] Failed to log success event:', logError);
    });
    return fromDashboard;
  }

  const url = `${API_BASE_URL}/shift/current-for-site?site_id=${encodeURIComponent(site_id)}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 404) return null;
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const err = new Error(errorData.error || errorData.message || `API Error: ${response.status}`);
      err.status = response.status;
      throw err;
    }
    const data = await response.json();
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_place_order',
      `Successfully fetched current shift for site`,
      {
        endpoint: '/shift/current-for-site',
        method: 'GET',
        siteId: site_id,
      }
    ).catch(logError => {
      console.warn('[placeOrderApi] Failed to log success event:', logError);
    });
    
    return data;
  } catch (error) {
    if (error.status) throw error;
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      const corsError = new Error('CORS Error: Unable to connect to the API. Please contact the backend team.');
      corsError.isCorsError = true;
      throw corsError;
    }
    throw error;
  }
};

/**
 * Fallback: get site shifts for today from dashboard and return first shift with ss_id.
 * GET /dashboard/site/shifts?site_id={site_id}&date=YYYY-MM-DD
 */
async function getCurrentShiftForSiteFallback(site_id) {
  try {
    const date = getTodayDate();
    const response = await makeApiRequest(`/dashboard/site/shifts?site_id=${encodeURIComponent(site_id)}&date=${date}`, 'GET');
    const data = await response.json();
    const shifts = data?.shifts;
    if (!Array.isArray(shifts) || shifts.length === 0) return null;
    const first = shifts.find((s) => s?.ss_id != null && String(s.ss_id).trim() !== '') || shifts[0];
    if (!first?.ss_id) return null;
    // Note: Success logging is handled in getCurrentShiftForSite to avoid duplicate logs
    return {
      ss_id: first.ss_id,
      shift_id: first.shift_id,
      shift_schedule_id: first.ss_id,
      shift_start_time: first.shift_start_time,
      shift_end_time: first.shift_end_time,
    };
  } catch {
    return null;
  }
}

/**
 * Get a single dish by ID.
 * GET /restaurant/dish?dish_id={dish_id}
 * Lambda: innoworx-restaurant-get-dish-by-id
 * 
 * @param {string} dish_id - The dish ID
 * @returns {Promise<Object>} Dish object
 */
export const getDishById = async (dish_id) => {
  if (!dish_id) {
    throw new Error('dish_id is required');
  }
  const response = await makeApiRequest(`/restaurant/dish?dish_id=${dish_id}`, 'GET');
  const data = await response.json();
  
  // Log API event for successful fetch
  loggingService.logInfo(
    'api_success_place_order',
    `Successfully fetched dish by ID`,
    {
      endpoint: '/restaurant/dish',
      method: 'GET',
      dishId: dish_id,
    }
  ).catch(logError => {
    console.warn('[placeOrderApi] Failed to log success event:', logError);
  });
  
  return data;
};

/**
 * Get all dishes for a site.
 * POST /restaurant/dish/getall-by-site?site_id={site_id}
 * Lambda: innoworx-restaurant-getall-dishes-by-site-id
 * 
 * Response structure: { site_id, dishes: [ { dish_id, site_id, Price, ... } ] }
 * 
 * @param {string} site_id - The site ID
 * @returns {Promise<Array>} Array of dish objects from the dishes array in the response
 */
export const getDishesBySite = async (site_id) => {
  if (!site_id) {
    throw new Error('site_id is required');
  }

  const params = new URLSearchParams();
  params.set('site_id', site_id);
  const endpoint = `/restaurant/dish/getall-by-site?${params.toString()}`;
  
  const response = await makeApiRequest(endpoint, 'POST');
  const data = await response.json();
  
  // Response structure: { site_id, dishes: [...] }
  const dishes = (data.dishes && Array.isArray(data.dishes)) ? data.dishes : (Array.isArray(data) ? data : []);
  
  // Log API event for successful fetch
  loggingService.logInfo(
    'api_success_place_order',
    `Successfully fetched dishes by site`,
    {
      endpoint: '/restaurant/dish/getall-by-site',
      method: 'POST',
      siteId: site_id,
      dishesCount: dishes.length,
    }
  ).catch(logError => {
    console.warn('[placeOrderApi] Failed to log success event:', logError);
  });
  
  if (dishes.length > 0) {
    return dishes;
  }
  
  // No dishes found - return empty array
  return [];
};

/**
 * Get tables for a site.
 * GET /tables/tables?site_id={site_id}
 * Lambda: innoworx-tables-table-list
 *
 * @param {string} site_id - Site ID (required)
 * @param {string} [section] - Optional section filter
 * @param {number} [floor_no] - Optional floor number
 * @param {string} [floor_name] - Optional floor name
 * @returns {Promise<Array>} Array of table objects: { site_id, table_no, table_id, floor_no, floor_name, section, capacity, table_label }
 */
export const getTablesBySite = async (site_id, { section, floor_no, floor_name } = {}) => {
  if (!site_id) return [];
  try {
    const params = new URLSearchParams();
    params.set('site_id', site_id);
    if (section) params.set('section', section);
    if (floor_no != null) params.set('floor_no', String(floor_no));
    if (floor_name) params.set('floor_name', floor_name);
    const response = await makeApiRequest(`/tables/tables?${params.toString()}`, 'GET');
    const data = await response.json();
    const list = data?.tables;
    const tables = Array.isArray(list) ? list : [];
    
    // Log API event for successful fetch
    loggingService.logInfo(
      'api_success_place_order',
      `Successfully fetched tables by site`,
      {
        endpoint: '/tables/tables',
        method: 'GET',
        siteId: site_id,
        tablesCount: tables.length,
        section,
        floorNo,
        floorName,
      }
    ).catch(logError => {
      console.warn('[placeOrderApi] Failed to log success event:', logError);
    });
    
    return tables;
  } catch (err) {
    console.warn('getTablesBySite failed:', err.message);
    return [];
  }
};

/**
 * Place an order (create or add to existing order).
 * POST /restaurant/order/place
 * Lambda: innoworx-restaurant-place-order
 * 
 * @param {Object} orderData - Order data
 * @param {Array} orderData.order_data - Array of order items: [{ dish_id, qty, notes?, cust_mods? }]
 * @param {string} orderData.site_id - Site ID
 * @param {string} orderData.ss_id - Shift schedule ID
 * @param {string} [orderData.table_id] - Table ID (UUID from GET /tables/tables); use when selecting from API tables
 * @param {number} [orderData.table_no] - Table number; use for manual entry when table_id not available
 * @param {string} [orderData.server_user_id] - Server user ID (defaults to Cognito sub if not provided)
 * @returns {Promise<Object>} Order response
 */
export const placeOrder = async ({ order_data, site_id, ss_id, table_id, table_no, server_user_id } = {}) => {
  if (!order_data || !Array.isArray(order_data) || order_data.length === 0) {
    throw new Error('order_data is required and must be a non-empty array');
  }
  if (!site_id) {
    throw new Error('site_id is required');
  }
  if (!ss_id) {
    throw new Error('ss_id (shift schedule ID) is required');
  }
  const hasTableId = table_id != null && String(table_id).trim() !== '';
  const hasTableNo = table_no !== null && table_no !== undefined && table_no !== '';
  if (!hasTableId && !hasTableNo) {
    throw new Error('Either table_id or table_no is required');
  }

  let bodyTableNo;
  if (hasTableId) {
    bodyTableNo = undefined;
  } else {
    let tableNumber;
    if (typeof table_no === 'number') {
      tableNumber = table_no;
    } else if (typeof table_no === 'string') {
      const num = parseInt(table_no, 10);
      if (isNaN(num)) {
        const match = table_no.match(/\d+/);
        tableNumber = match ? parseInt(match[0], 10) : NaN;
      } else {
        tableNumber = num;
      }
    } else {
      tableNumber = NaN;
    }
    if (tableNumber <= 0 || !Number.isInteger(tableNumber)) {
      throw new Error('table_no must be a positive integer');
    }
    bodyTableNo = tableNumber;
  }

  // Validate order_data structure
  for (const item of order_data) {
    if (!item.dish_id) {
      throw new Error('Each order item must have a dish_id');
    }
    if (!item.qty || item.qty <= 0) {
      throw new Error('Each order item must have a valid qty > 0');
    }
  }

  const body = {
    order_data: order_data.map(item => ({
      dish_id: item.dish_id,
      qty: item.qty,
      ...(item.notes && { notes: item.notes }),
      ...(item.cust_mods && { cust_mods: item.cust_mods }),
    })),
    site_id,
    ss_id,
    ...(hasTableId && { table_id: String(table_id).trim() }),
    ...(bodyTableNo !== undefined && { table_no: bodyTableNo }),
    ...(server_user_id && { server_user_id }),
  };

  const response = await makeApiRequest('/restaurant/order/place', 'POST', body);
  const data = await response.json();
  
  // Log API event for successful order placement
  loggingService.logInfo(
    'api_success_place_order',
    `Successfully placed order`,
    {
      endpoint: '/restaurant/order/place',
      method: 'POST',
      siteId: site_id,
      ssId: ss_id,
      itemsCount: order_data.length,
      tableId: hasTableId ? table_id : null,
      tableNo: bodyTableNo !== undefined ? bodyTableNo : null,
    }
  ).catch(logError => {
    console.warn('[placeOrderApi] Failed to log success event:', logError);
  });
  
  return data;
};

export default {
  getSiteDataset, // Note: For analytics only, not for site selection
  getCurrentShiftForSite,
  getDishById,
  getDishesBySite,
  getTablesBySite,
  placeOrder,
};

